import { z } from 'zod';
import {
  auditFieldsSchema,
  codiceFiscaleSchema,
  emailSchema,
  indirizzoSchema,
  partitaIvaSchema,
  telefonoSchema,
} from '../common';

/**
 * Anagrafica dell'azienda che USA il gestionale — non un cliente e non un
 * partner: serve alle intestazioni dei documenti generati, a partire dal
 * verbale di fase. È una riga sola, quindi non ha né lista né creazione: si
 * legge e si aggiorna.
 */
export const impostazioniAziendaBaseSchema = z.object({
  ragioneSociale: z.string().trim().min(1).max(255),
  codiceFiscale: codiceFiscaleSchema.optional(),
  partitaIva: partitaIvaSchema.optional(),
  sedeLegale: indirizzoSchema,
  email: emailSchema.optional(),
  pec: emailSchema.optional(),
  telefono: telefonoSchema.optional(),
  sitoWeb: z.url('Indirizzo del sito non valido').trim().max(200).optional(),
});

/**
 * Un'intestazione senza codice fiscale né partita IVA non identifica nessuno:
 * almeno uno dei due è obbligatorio.
 */
export const impostazioniAziendaSchema = impostazioniAziendaBaseSchema.superRefine((i, ctx) => {
  if (i.codiceFiscale == null && i.partitaIva == null) {
    ctx.addIssue({
      code: 'custom',
      message: 'Indica almeno il codice fiscale o la partita IVA',
      path: ['partitaIva'],
    });
  }
});
export type ImpostazioniAziendaInput = z.infer<typeof impostazioniAziendaSchema>;

export const impostazioniAziendaVistaSchema = impostazioniAziendaBaseSchema.extend(
  auditFieldsSchema.shape,
);
export type ImpostazioniAzienda = z.infer<typeof impostazioniAziendaVistaSchema>;

/**
 * Quanto prima partono i promemoria, per tutto lo studio. Le scadenze di
 * progetto sono poche e pesanti, quindi hanno più soglie; i task sono tanti,
 * quindi un solo avviso: a ore se la scadenza ha un orario, a giorni se no.
 * `null` (o un elenco vuoto) spegne quel promemoria.
 */
export const impostazioniPromemoriaSchema = z.object({
  /** Giorni prima della scadenza di progetto; 0 = il giorno stesso. */
  scadenzeGiorniPrima: z
    .array(z.number().int().min(0).max(60))
    .max(6)
    .refine(
      (giorni) => new Set(giorni).size === giorni.length,
      'Hai indicato due volte lo stesso giorno',
    ),
  /** Minuti prima di un task con orario. */
  taskConOrarioMinutiPrima: z.number().int().min(5).max(2880).nullable(),
  /** Giorni prima di un task senza orario; 0 = il giorno stesso. */
  taskSenzaOrarioGiorniPrima: z.number().int().min(0).max(14).nullable(),
});
export type ImpostazioniPromemoria = z.infer<typeof impostazioniPromemoriaSchema>;

/** Valori finché nessuno li ha cambiati. */
export const PROMEMORIA_PREDEFINITI: ImpostazioniPromemoria = {
  scadenzeGiorniPrima: [7, 1, 0],
  taskConOrarioMinutiPrima: 60,
  taskSenzaOrarioGiorniPrima: 1,
};
